import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { Card } from "@omni/ui/components/Card/card";
import { useEffect, useRef, useState } from "react";

import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { GiftCardStatus } from "@/utils/enums";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import { GiftCardField } from "../Form/RHF/FormFields/GiftCardField/giftCardField.component";
import { RecaptchaField } from "../Form/RHF/FormFields/RecaptchaField/recaptchaField.component";
import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import GiftCardBalance from "./giftCardBalance/giftCardBalance.component";
import {
    GiftCardRequestBalanceFormValidationSchema,
    GiftCardRequestBalanceFormValues
} from "./giftCardRequestBalance.schema";

const FORM_ID = "giftCardBalanceRequestForm";

export type GiftCardInfo = {
    balance: number;
    giftCardStatus: any;
    expirationDate: string;
};

type Props = {
    onRequestBalanceFor: (giftCardNumber: string, recaptcha: string) => void;
    initialGiftCardNumber: string | null;
    info: GiftCardInfo;
    error: any;
    loading: boolean;
};

const GiftCardRequestBalance = ({
    onRequestBalanceFor,
    info,
    error,
    loading,
    initialGiftCardNumber
}: Props) => {
    const { t } = useGranite();
    const recaptchaRef: any = useRef(null);
    const [balance, setBalance] = useState<number | undefined>(0);
    const [giftCardStatus, setGiftCardStatus] = useState(null);
    const [expirationDate, setExpirationDate] = useState("");

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            giftCardNumber: initialGiftCardNumber ?? "",
            recaptcha: ""
        },
        resolver: yupResolver(GiftCardRequestBalanceFormValidationSchema)
    });

    useEffect(() => {
        const callback = methods.subscribe({
            name: ["giftCardNumber"],
            callback: () => {
                setBalance(undefined);
                setExpirationDate("");
                setGiftCardStatus(null);
            }
        });

        return () => callback();
    }, [methods.subscribe]);

    useEffect(() => {
        if (info) {
            setBalance(info.balance);
            setGiftCardStatus(info.giftCardStatus);
            setExpirationDate(info.expirationDate);
        }
    }, [info]);

    const resetReCaptcha = () => {
        recaptchaRef.current?.reset();
        methods.setValue("recaptcha", "", { shouldTouch: false });
        methods.clearErrors("recaptcha");
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (giftCardStatus) {
            if (
                [GiftCardStatus.Blocked, GiftCardStatus.Locked].some(
                    (s) => s === giftCardStatus
                )
            ) {
                broadcastMessage({
                    id: "giftcard-balance-request-blocked",
                    text: t(
                        "general.form.giftCardRequestBalance.error.blocked"
                    ),
                    status: "danger",
                    dismissible: true
                });
            }
            if (giftCardStatus === GiftCardStatus.Expired) {
                broadcastMessage({
                    id: "giftcard-balance-request-expired",
                    text: t(
                        "general.form.giftCardRequestBalance.error.expired"
                    ),
                    status: "danger",
                    dismissible: true
                });
            }
            if (giftCardStatus === GiftCardStatus.NotFound) {
                broadcastMessage({
                    id: "giftcard-balance-request-not-found",
                    text: t(
                        "general.form.giftCardRequestBalance.error.not.found"
                    ),
                    status: "danger",
                    dismissible: true
                });
            }
        } else {
            clearMessages();
        }
    }, [giftCardStatus]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!error) {
            return;
        }
        if ([500, 503].some((e) => e === error?.response?.status)) {
            broadcastMessage({
                id: "giftcard-balance-request-not-available",
                text: t(
                    "general.form.giftCardRequestBalance.error.not.available"
                ),
                status: "danger",
                dismissible: true
            });
        } else {
            methods.setError("giftCardNumber", {
                message: () => (t) => t("form.validation.giftCard.invalid")
            });
        }
    }, [error]);

    const clearMessages = () => {
        clearMessageById("giftcard-balance-request-not-available");
        clearMessageById("giftcard-balance-request-expired");
        clearMessageById("giftcard-balance-request-blocked");
        clearMessageById("giftcard-balance-request-not-found");
    };

    const onFormSubmit = ({
        giftCardNumber,
        recaptcha
    }: GiftCardRequestBalanceFormValues) => {
        resetReCaptcha();
        clearMessages();
        onRequestBalanceFor(giftCardNumber, recaptcha);
    };

    return (
        <div>
            <Card>
                <Card.Body>
                    <Messages
                        messageid={"giftcard-balance-request-not-available"}
                    />
                    <Messages messageid={"giftcard-balance-request-expired"} />
                    <Messages messageid={"giftcard-balance-request-blocked"} />
                    <Messages
                        messageid={"giftcard-balance-request-not-found"}
                    />
                    <FormInfoProvider
                        formId={FORM_ID}
                        withNeverbounce={false}
                    >
                        {() => (
                            <OmniFormContextProvider {...methods}>
                                <form
                                    id={FORM_ID}
                                    onSubmit={methods.handleSubmit(
                                        onFormSubmit
                                    )}
                                >
                                    <Row>
                                        <Col>
                                            <GiftCardField<GiftCardRequestBalanceFormValues>
                                                test="giftCardForm-input-cardCode"
                                                name="giftCardNumber"
                                                hideLabel={true}
                                                label={t(
                                                    "checkout.paymentMethods.giftCard"
                                                )}
                                                required
                                                disabled={loading}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <RecaptchaField<GiftCardRequestBalanceFormValues>
                                                ref={recaptchaRef}
                                                name="recaptcha"
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <Button
                                                test="giftCardRequestBalance-button-submit"
                                                type="submit"
                                                size="md"
                                                loading={loading}
                                            >
                                                {t(
                                                    "general.form.giftCardRequestBalance.submit"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </form>
                            </OmniFormContextProvider>
                        )}
                    </FormInfoProvider>
                </Card.Body>
            </Card>
            {[GiftCardStatus.Valid, GiftCardStatus.NoBalance].some(
                (s) => s === giftCardStatus
            ) &&
                balance !== undefined &&
                expirationDate && (
                    <GiftCardBalance
                        balance={balance}
                        expirationDate={expirationDate}
                    />
                )}
        </div>
    );
};

export default GiftCardRequestBalance;
